import { useCallback, useRef, useState } from 'react';
import { usePhotoCropFlow } from './usePhotoCropFlow';

interface UseGalleryPhotoCropUploadOptions {
  validateImageFile: (file: File) => string | null;
  uploadGalleryFile: (file: File, slotIndex: number) => Promise<void>;
  onValidationError?: (message: string) => void;
}

export function useGalleryPhotoCropUpload({
  validateImageFile,
  uploadGalleryFile,
  onValidationError,
}: UseGalleryPhotoCropUploadOptions) {
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const activeSlotRef = useRef<number | null>(null);
  const pendingFilesRef = useRef<File[]>([]);
  const openCropRef = useRef<(file: File) => boolean>(() => false);

  const [uploadingSlotIndex, setUploadingSlotIndex] = useState<number | null>(null);

  const processNextQueuedFile = useCallback(() => {
    const [nextFile, ...remainingFiles] = pendingFilesRef.current;
    pendingFilesRef.current = remainingFiles;

    if (nextFile) {
      openCropRef.current(nextFile);
    } else {
      activeSlotRef.current = null;
    }
  }, []);

  const handleGalleryUpload = useCallback(
    async (file: File) => {
      const slotIndex = activeSlotRef.current;
      if (slotIndex == null) return;

      setUploadingSlotIndex(slotIndex);
      try {
        await uploadGalleryFile(file, slotIndex);
      } finally {
        setUploadingSlotIndex(null);
        processNextQueuedFile();
      }
    },
    [processNextQueuedFile, uploadGalleryFile]
  );

  const {
    isCropOpen,
    cropImageSrc,
    cropFileMeta,
    isApplyingCrop,
    closeCrop,
    handleCropApply,
    openCropForFile,
  } = usePhotoCropFlow({
    validateImageFile,
    onUpload: handleGalleryUpload,
    onValidationError,
  });

  openCropRef.current = openCropForFile;

  const openGalleryPicker = useCallback((slotIndex: number) => {
    activeSlotRef.current = slotIndex;
    galleryInputRef.current?.click();
  }, []);

  const handleGalleryFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? []);
      event.target.value = '';

      if (!files.length || activeSlotRef.current == null) return;

      const validationError = validateImageFile(files[0]);
      if (validationError) {
        onValidationError?.(validationError);
        activeSlotRef.current = null;
        return;
      }

      pendingFilesRef.current = files.slice(1);
      openCropForFile(files[0]);
    },
    [onValidationError, openCropForFile, validateImageFile]
  );

  const isGallerySlotUploading = useCallback(
    (slotIndex: number) =>
      uploadingSlotIndex === slotIndex || (isApplyingCrop && activeSlotRef.current === slotIndex),
    [isApplyingCrop, uploadingSlotIndex]
  );

  return {
    galleryInputRef,
    openGalleryPicker,
    handleGalleryFileChange,
    isGallerySlotUploading,
    isCropOpen,
    cropImageSrc,
    cropFileMeta,
    isApplyingCrop,
    closeCrop,
    handleCropApply,
  };
}
